import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  createContract,
  getContracts,
  getContractDefaults,
  type ContractIndexKeyEntries,
  type ContractKeyEntries,
} from './contracts.api'
import type { CreateContractInput } from './contracts.types'
import { QueryFilters } from '@/lib/utils'

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type ContractIndexQueryKey = [AdminQueryRoot & ContractIndexKeyEntries]
type ContractDefaultsQueryKey = [AdminQueryRoot & ContractKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const contractFilters = () =>
  new QueryFilters({
    include: ['organization'],
  })

export const contractQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'contract' }, { organizationId }),
  ],
  index: ({ organizationId, filters }: ContractIndexKeyEntries): ContractIndexQueryKey =>
    buildScopedAdminQueryKey(contractQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  defaults: ({ organizationId }: ContractKeyEntries): ContractDefaultsQueryKey =>
    buildScopedAdminQueryKey(contractQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'defaults',
    }),
}

export const contractQueries = {
  index: ({ organizationId, filters }: ContractIndexKeyEntries) =>
    indexTableQueryOptions({
      queryKey: contractQueryKeys.index({ organizationId, filters }),
      queryFn: getContracts,
    }),
  defaults: ({ organizationId }: ContractKeyEntries) =>
    queryOptions({
      queryKey: contractQueryKeys.defaults({ organizationId }),
      queryFn: getContractDefaults,
    }),
}

export function useContracts({
  organizationId,
  filters,
  params = {},
}: OrganizationScope &
  Pick<ContractIndexKeyEntries, 'filters'> & {
    params?: QueryParams<ReturnType<typeof contractQueries.index>>
  } = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...contractQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useCreateContractMutation() {
  const organizationId = useRouteOrganizationId()
  return useMutation({
    mutationFn: (values: CreateContractInput) => createContract({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: contractQueryKeys.all({ organizationId }) }),
  })
}

export function useContractDefaults({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof contractQueries.defaults>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...contractQueries.defaults({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}
